<script setup lang="ts">
import {type ObjectId, type Tag, translate} from "~/composables/charon";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

const { charon } = useCharon()
const { t, locale } = useI18n()

const props = defineProps<{
  tagId: ObjectId
  expectedLocales: string[]
}>()

const emit = defineEmits<{
  (e: 'deleted'): void
  (e: 'remove'): void
  (e: 'updated'): void
}>()

const open = ref(false)

const { data: tag, refresh } = useAsyncData(
  `tag-${props.tagId}`,
  () => charon<Tag>(`/tags/${props.tagId}`),
  {
    lazy: true,
    watch: [() => props.tagId]
  }
)

const translated = computed(() => translate(tag.value?.locales, locale.value))

const warn = computed(() => {
  const tagLocales = Object.keys(tag.value?.locales ?? {})
  return props.expectedLocales.some(locale => !tagLocales.includes(locale))
})

function deleted() {
  emit('deleted')
}

function remove() {
  emit('remove')
}

function updated() {
  refresh()
  open.value = false
  emit('updated')
}
</script>

<template>
  <div
    v-if="tag"
    class="flex flex-row items-center bg-gray-100 px-1.5 my-1 rounded data-[warn=true]:bg-amber-500"
    :data-warn="warn"
    :title="t('jc.component.tagInputItem.warning')"
  >
    <font-awesome-icon
      v-if="warn"
      class="mr-1"
      icon="triangle-exclamation"
      fixed-width
      size="sm"
    />
    <button
      class="my-auto whitespace-nowrap text-gray-900
        hover:text-primary-highlighted active:text-primary-selected
        data-[warn=true]:hover:text-white data-[warn=true]:active:text-gray-200"
      @click="open = true"
      :title="translated?.description"
      :data-warn="warn"
    >
      {{ translated?.label }}
    </button>
    <button
      class="flex text-gray-900 ml-1
        hover:text-primary-highlighted active:text-primary-selected
        data-[warn=true]:hover:text-white data-[warn=true]:active:text-gray-200"
      @click="remove"
      :title="t('jc.component.tagInputItem.removeButton.title')"
      :data-warn="warn"
    >
      <span class="sr-only">{{ t('jc.component.tagInputItem.removeButton.label') }}</span>
      <font-awesome-icon
        icon="xmark"
        fixed-width
        size="sm"
      />
    </button>
    <TagEditor
      :tag="tag"
      :open="open"
      :namespace="tag.namespace"
      @updated="updated"
      @deleted="deleted"
      @cancel="open = false"
    />
  </div>
</template>
